import * as React from 'react'

import { Button } from '@factorialco/deprecated-design-system'
import { Form } from 'factorial-form'
import moment from 'moment'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { stringToDate } from 'modules/core/lib/dates'
import { employeeLegalEntity } from 'modules/core/lib/employeeLegalEntity'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import { employeeUser } from 'modules/core/store/collections/users'
import app from 'modules/core/store/models/app'

import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import { useTerminateEmployee } from 'modules/employees/hooks/useTerminateEmployee'
import { employeesThatCanBeMyManager } from 'modules/employees/store/collections/employeesContractVersions'
import Employee from 'modules/employees/store/models/Employee'
import { employeeJob } from 'modules/job_catalog/store/collections/roles'
import terminationTypes from 'modules/payroll_bundle/store/collections/terminationTypes'

import FirstStep from './FirstStep'

interface Props {
  employee: Employee
  contractVersions: ContractVersions
  onClose: () => void
  onSuccess: () => void
}

const TerminationModal: React.FC<Props> = ({ employee, contractVersions, onClose, onSuccess }) => {
  const { terminate } = useTerminateEmployee()
  const [isConfirming, setIsConfirming] = React.useState<boolean>(false)

  const fetchTerminationTypes = async () => {
    await terminationTypes.fetch()
  }

  useEffect(() => {
    fetchTerminationTypes()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const initialManagerId: number | undefined =
    employee.manager?.id || employeesThatCanBeMyManager(employee, contractVersions)[0]?.id

  const terminateFormValues = {
    pre_warning: true,
    manager_id: initialManagerId,
  }

  const form = useLazyRef(
    () =>
      new Form(terminateFormValues, {
        terminated_on: 'date',
        termination_type_id: 'number',
        termination_reason_type: 'string',
        termination_reason: 'string',
        termination_observations: 'string',
        manager_id: 'number',
        stop_pending_approvals: 'boolean',
      })
  ).current

  const isFiredByCompany = (): boolean => {
    return form.get('termination_reason_type').value == Employee.TERMINATION_REASON_TYPE.company
  }

  const isTerminationDateValid = (): boolean => {
    const date = form.get('terminated_on').value

    return date !== '' && stringToDate(date, 'ui').isValid()
  }

  const fullName = employeeUser(employee).fullName()

  const terminationDate = form.data().terminated_on

  const isEligibleForPayroll = employee.isEligibleForPayroll //old payroll we can refactor and get rid of it

  const isSubmitDisabled = !isTerminationDateValid() || !form.data().termination_reason_type

  const onClickTerminate = () => {
    setIsConfirming(true)
  }

  const terminateData = () => {
    const data = form.data()
    return {
      id: employee.id,
      terminated_on: terminationDate,
      termination_type_id: data.termination_type_id,
      termination_reason: data.termination_reason,
      termination_reason_type: data.termination_reason_type,
      termination_assigned_manager_id: data.manager_id,
      stop_pending_approvals: data.stop_pending_approvals === true ? true : false,
    }
  }
  const legalEntity = employeeLegalEntity(employee)

  const onSubmit = async (closePortal: ClosePortalFn) => {
    if (!isConfirming || !employee.policy.can('terminateEmployee')) return
    const data = terminateData()

    const handled = handleResult(
      await terminate({
        id: employee.id,
        terminationAssignedManagerId: data.termination_assigned_manager_id,
        terminatedOn: terminationDate,
        terminationReason: data.termination_reason,
        terminationReasonType: data.termination_reason_type,
        terminationTypeId: data.termination_type_id,
        stopPendingApprovals: data.stop_pending_approvals,
      }),
      () => undefined
    )
    if (handled.success) {
      closePortal()
      onSuccess()
    } else {
      const error = handled.errors[0]
      const message = () => {
        if (error && 'message' in error) {
          return error.message
        }
        if (error && 'messages' in error) {
          return error.messages[0] || i18n.t('employees.termination.default_error')
        }
        return i18n.t('employees.termination.default_error')
      }

      app.setFlash({
        type: 'wrong',
        message: message(),
        time: 2000,
      })
      setIsConfirming(false)
    }

    const terminationReason = form.data().termination_type_id
      ? terminationTypes.get(form.data().termination_type_id)?.attributes.slug || '-'
      : form.data().termination_reason_type

    track(trackingEvents.employee.termination.terminateSubmit, {
      company_id: legalEntity.attributes.company_id,
      user_id: employee.id,
      user_role: employeeJob(contractVersions, employee),
      time_stamp: Date.now(),
      fields_edited: form.dirtyFieldsKeys,
      reason_of_termination: terminationReason,
      is_successful: handled.success,
      error_code: !handled.success && JSON.stringify(handled.errors[0]),
    })
  }

  const onCancel = () => {
    if (!isConfirming) {
      track(trackingEvents.employee.termination.terminateCancel, {
        company_id: legalEntity.attributes.company_id,
        user_id: employee.id,
        user_role: employeeJob(contractVersions, employee),
        time_stamp: Date.now(),
        fields_edited: form.dirtyFieldsKeys,
      })
    }
    onClose()
  }

  const onBackToFirstStep = () => {
    setIsConfirming(false)
  }

  const description = (): string => {
    const today = moment().format('YYYY-MM-DD')
    if (isConfirming) {
      if (terminationDate < today) {
        return i18n.t('employees.termination.modal_description_past_date', {
          date: form.get('terminated_on').value,
        })
      }
      return i18n.t('employees.termination.modal_description_future_date')
    } else {
      return i18n.t('employees.termination.modal_description')
    }
  }

  return (
    <Modal
      illustration="user-remove"
      title={i18n.t('employees.termination.action', { name: fullName })}
      description={description()}
      onSubmit={onSubmit}
      onClose={() => onCancel()}
      footer={
        <>
          {!isConfirming && (
            <Button onClick={onClickTerminate} type="negative" disabled={isSubmitDisabled}>
              {i18n.t('employees.termination.action', {
                name: fullName,
              })}
            </Button>
          )}
          {isConfirming && (
            <>
              <Button onClick={onBackToFirstStep} type="secondary">
                {i18n.t('common.back')}
              </Button>
              <Button submit type="negative">
                {i18n.t('employees.termination.action', {
                  name: fullName,
                })}
              </Button>
            </>
          )}
        </>
      }
    >
      {!isConfirming && (
        <FirstStep
          employee={employee}
          isFiredByCompany={isFiredByCompany()}
          isEligibleForPayroll={isEligibleForPayroll}
          form={form}
          contractVersions={contractVersions}
        />
      )}
    </Modal>
  )
}

export default observer(TerminationModal)
